import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import './FormControls.css';

interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
  labelExtra?: ReactNode;
  errorMessage?: string | null | undefined;
}

/** Labeled password input with a show/hide toggle. Justified as a shared primitive because
 * feature/admin-password-recovery's New Password screen is already known to need the identical control. */
export function PasswordField({ label, labelExtra, errorMessage, ...inputProps }: PasswordFieldProps) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  const errorId = errorMessage ? `${id}-error` : undefined;

  return (
    <div className="form-field">
      <div className="form-field-label-row">
        <label htmlFor={id}>{label}</label>
        {labelExtra}
      </div>
      <div className="password-field-wrap">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          aria-invalid={errorMessage ? true : undefined}
          aria-describedby={errorId}
          {...inputProps}
        />
        <button
          type="button"
          className="password-field-toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {errorMessage && (
        <p id={errorId} className="form-field-error" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path fill="none" stroke="currentColor" strokeWidth="2" d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.24 4.24M9.9 4.24A11 11 0 0 1 12 4c7 0 11 7 11 7a13.2 13.2 0 0 1-3.15 3.9M6.5 6.6C3.9 8.3 2 11 2 11s4 7 11 7c1.4 0 2.7-.27 3.87-.74"
      />
    </svg>
  );
}
